import {
  BacktestingJobResults,
  BacktestingLineChartType,
  BacktestingRunType,
  BacktestingStatus,
  JobStatus,
} from "@shared/types";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { useJobQuery } from "@shared/services/jobs/hooks";
import { getFormattedChartXValue } from "@shared/utils/charts";
import usePeriodicStats from "@shared/features/bots/BotForm/Backtesting/usePeriodicStats";
import { useState } from "react";
import useLiveBacktesting from "@shared/features/bots/BotForm/Backtesting/useLiveBacktesting";

interface useBacktestingChartProps {
  backtestingRun?: BacktestingRunType;
}

const useBacktestingChart = ({ backtestingRun }: useBacktestingChartProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const displayCurrencyPrecision = getDisplayCurrencyAmountPrecision();
  const [includeInitialFunds, setIncludeInitialFunds] = useState(false);

  const isOriginallyInProgress =
    (backtestingRun?.original_status ?? backtestingRun?.status) ===
    BacktestingStatus.IN_PROGRESS;
  const jobQueryStatus = useJobQuery(
    isOriginallyInProgress ? backtestingRun?.job_id : undefined,
    true
  );
  const backtestingJobResults = jobQueryStatus?.data
    ?.data as BacktestingJobResults;
  const livePeriodicStatsData = backtestingJobResults?.periodic_stats_data;

  const shouldFetchPeriodicStats =
    backtestingRun?.id &&
    !livePeriodicStatsData &&
    (!isOriginallyInProgress ||
      jobQueryStatus?.data?.status === JobStatus.FINISHED);

  const {
    data,
    isFetching: backtestingPeriodicStatsFetching,
    isSuccess,
  } = sharedLib.sharedApi.bots.useGetBacktestingPeriodicStatsQuery(
    shouldFetchPeriodicStats
      ? { backtesting_run_id: backtestingRun.id }
      : skipToken
  );

  const isFetching =
    backtestingPeriodicStatsFetching ||
    (!data?.length && !livePeriodicStatsData?.data.length);

  const { liveBacktestingData } = useLiveBacktesting({ data: backtestingRun });
  const { realizedPnlLineChart, netPnlLineChart } = usePeriodicStats({
    backtestingRun: liveBacktestingData,
    periodicStatsData: livePeriodicStatsData,
    periodicStats: data,
    includeInitialFunds,
  });

  const lineChartMultiData = [
    {
      name: BacktestingLineChartType.REALIZED_PNL,
      data: realizedPnlLineChart,
    },
    {
      name: BacktestingLineChartType.NET_PNL,
      data: netPnlLineChart,
    },
  ];

  const xMinMax = backtestingRun?.backtesting_time_range
    ? {
        min: backtestingRun?.backtesting_time_range?.start,
        max: backtestingRun?.backtesting_time_range?.end,
      }
    : undefined;
  const strXMinMax = xMinMax
    ? {
        min: getFormattedChartXValue(xMinMax.min) as string,
        max: getFormattedChartXValue(xMinMax.max) as string,
      }
    : undefined;

  const includeInitialFundsData = {
    checked: includeInitialFunds,
    onChange: setIncludeInitialFunds,
    text: "Include Initial Funds",
  };

  return {
    lineChartMultiData,
    isFetching,
    isSuccess,
    displayCurrency,
    strXMinMax,
    xMinMax,
    displayCurrencyPrecision,
    includeInitialFundsData,
  };
};

export default useBacktestingChart;
